import isEmail from 'validator/es/lib/isEmail';
import { EmployeeType, IEmployeeSite, IInvite, InvitationDisplay, SiteRole } from 'types';
import { orgRoleDisplay } from 'utils/employee';
import { formatMobile } from './strings';

/**
 * Formats an invite recipient's contact information.
 * If the recipient is not an email address, it formats the recipient as a mobile number.
 *
 * @param {string} recipient - The contact information of the invite recipient. It can be either an email address or a mobile number.
 *
 * @returns {string} - Returns the formatted recipient.
 *                     If the recipient is an email address, it returns the email address as is.
 *                     If the recipient is not an email address, it formats and returns the recipient as a mobile number.
 */
export const formatInviteRecipient = (recipient: string): string => {
  if (!isEmail(recipient)) return formatMobile(recipient);
  return recipient;
};

/**
 * Generates a list of sites/org to which the invitee was invited.
 *
 * @param {IInvite[]} invites - An array of Invite objects.
 *
 * @returns {InvitationDisplay[]} - An array of objects containing:
 *                                  - `orgName`: The name of the organization.
 *                                  - `role`: The role of the invitee at the site.
 *                                  - `displayRole`: The role of the invitee that will be displayed on the UI.
 *                                  - `siteName`: The name of the site.
 */
export const getInvitedSitesList = (invites: IInvite[]): InvitationDisplay[] => {
  const list: InvitationDisplay[] = [];
  invites.forEach((invite: IInvite) => {
    const orgName = invite?.employee?.organization.name;
    const isAdmin = invite?.employee?.type === EmployeeType.ADMIN;
    if (isAdmin) {
      return list.push({
        orgName: orgName || '',
        role: SiteRole.ADMIN,
        displayRole: orgRoleDisplay(invite.employee.type),
      });
    }
    invite.employee.sites.forEach((site: IEmployeeSite) =>
      list.push({
        orgName: orgName || '',
        role: site.role,
        displayRole: orgRoleDisplay(invite.employee.type, site.role) || '',
        siteName: site.name,
      })
    );
    return null;
  });

  return list;
};
